// On-screen numpad modal — popped open by NumberInput's double-click
// handler (and any other caller that wants a tablet-friendly numeric
// entry surface). Bring-your-own commit handler keeps it stateless
// outside the local display string.
//
// Keyboard: Esc closes, Enter commits. Step +/- buttons let Justin tap
// up/down in fixed increments (handy for the 1/16" thickness flow at
// step=0.0625).

import { useEffect, useState } from 'react';

export interface NumpadProps {
  open: boolean;
  initialValue?: string;
  title?: string;
  unit?: string;
  /** Snap-to step for + / - buttons. Default 1. */
  step?: number;
  /** Optional bounds for + / - buttons. */
  min?: number;
  max?: number;
  onCommit: (value: number) => void;
  onClose: () => void;
}

/** Round a number to a friendly precision based on the step size.
 *  Avoids the classic 0.0625 + 0.0625 = 0.12500000000000003 trail. */
function snap(value: number, step: number): string {
  if (!Number.isFinite(value)) return '';
  // Pick decimal places from the step: 0.0625 → 4 decimals, 1 → 0, etc.
  const stepStr = String(step);
  const dot = stepStr.indexOf('.');
  const decimals = dot === -1 ? 0 : stepStr.length - dot - 1;
  return value.toFixed(decimals).replace(/\.?0+$/, '') || '0';
}

export function Numpad({
  open,
  initialValue = '',
  title,
  unit,
  step = 1,
  min,
  max,
  onCommit,
  onClose,
}: NumpadProps) {
  const [display, setDisplay] = useState<string>(initialValue);

  // Reset display whenever the modal (re)opens.
  useEffect(() => {
    if (open) setDisplay(initialValue);
  }, [open, initialValue]);

  // Esc/Enter handlers — only active while open.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        commit();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // commit closes over display — rebind on each change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, display, onClose]);

  if (!open) return null;

  function clampToBounds(n: number): number {
    let out = n;
    if (typeof min === 'number' && out < min) out = min;
    if (typeof max === 'number' && out > max) out = max;
    return out;
  }

  function append(ch: string) {
    setDisplay((prev) => {
      // Block second decimal point.
      if (ch === '.' && prev.includes('.')) return prev;
      // Don't lead with multiple zeros — collapse "00" → "0".
      if (prev === '0' && ch !== '.') return ch;
      return prev + ch;
    });
  }

  function backspace() {
    setDisplay((prev) => prev.slice(0, -1));
  }

  function clear() {
    setDisplay('');
  }

  function bumpBy(delta: number) {
    const current = Number.parseFloat(display);
    const base = Number.isFinite(current) ? current : 0;
    const next = clampToBounds(base + delta);
    setDisplay(snap(next, step));
  }

  function commit() {
    const parsed = Number.parseFloat(display);
    if (!Number.isFinite(parsed)) {
      onClose();
      return;
    }
    onCommit(clampToBounds(parsed));
  }

  const gridButtons: Array<{ label: string; onClick: () => void; kind?: 'digit' | 'back' | 'dot' }> = [
    { label: '1', onClick: () => append('1') },
    { label: '2', onClick: () => append('2') },
    { label: '3', onClick: () => append('3') },
    { label: '4', onClick: () => append('4') },
    { label: '5', onClick: () => append('5') },
    { label: '6', onClick: () => append('6') },
    { label: '7', onClick: () => append('7') },
    { label: '8', onClick: () => append('8') },
    { label: '9', onClick: () => append('9') },
    { label: '.', onClick: () => append('.'), kind: 'dot' },
    { label: '0', onClick: () => append('0') },
    { label: '⌫', onClick: backspace, kind: 'back' },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-navy/60 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="numpad-title"
    >
      <div
        className="bg-paper rounded shadow-lg w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-2.5 border-b border-line flex items-center justify-between bg-navy text-paper">
          <h2 id="numpad-title" className="font-bold text-sm truncate">
            {title ?? 'Enter value'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-paper/80 hover:text-paper text-lg leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex items-baseline justify-end gap-2 bg-bg border border-line-dark rounded px-3 py-2">
            <span className="text-3xl font-mono font-semibold text-navy tabular-nums tracking-tight">
              {display || '0'}
            </span>
            {unit && <span className="text-sm text-text-mute">{unit}</span>}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {gridButtons.map((b) => (
              <button
                key={b.label}
                type="button"
                onClick={b.onClick}
                className={`py-3 text-lg font-semibold rounded border border-line-dark bg-paper text-navy hover:border-orange hover:bg-orange/5 active:bg-orange/10 transition-colors ${
                  b.kind === 'back' ? 'text-orange' : ''
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => bumpBy(-step)}
              className="py-2 text-sm font-semibold rounded border border-line-dark bg-paper text-navy hover:border-orange hover:bg-orange/5 transition-colors"
              aria-label={`Decrease by ${step}`}
            >
              − {step}
            </button>
            <button
              type="button"
              onClick={clear}
              className="py-2 text-sm font-semibold rounded border border-line-dark bg-paper text-text-mute hover:border-orange hover:text-orange transition-colors"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => bumpBy(step)}
              className="py-2 text-sm font-semibold rounded border border-line-dark bg-paper text-navy hover:border-orange hover:bg-orange/5 transition-colors"
              aria-label={`Increase by ${step}`}
            >
              + {step}
            </button>
          </div>

          <button
            type="button"
            onClick={commit}
            className="w-full py-3 text-base font-bold rounded bg-orange text-white hover:opacity-90 transition-opacity"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
